import {
  CAlert,
  CButton,
  CButtonGroup,
  CCol,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import {
  createItem,
  updateItem,
  ItemRequestStatus,
  getList,
} from "../../api/generics";
import AccountingEntry, {
  newAccountingEntry,
} from "../../models/accounting-entries";
import Datetime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import moment from "moment";
import Errors, { getFieldErrors } from "../../models/errors";
import { SUCCESS } from "../../utils/constants/tags";
import { FieldErrors } from "../form/FieldErrors";
import { errorAlert } from "../utils/messages";
import { emptyValueOnUndefined } from "../../utils/fields";
import { RootState } from "../../store";
import { useSelector } from "react-redux";

import AccountingEntriesGroup, {
  newAccountingEntryGroup,
} from "../../models/accounting-entries-group";
import {
  listToUniqueListItems,
  newUniqueListItem,
  UniqueListItem,
  uniqueListToListItems,
} from "../../models/unique-list-item";
import AccountingEntryFormItem from "./AccountingEntryFormItem";

interface AccountingEntryFormProps {
  initialAccountingEntriesGroup?: AccountingEntry;
  initialErrors?: Errors;
  onCancel: () => void | Promise<void>;
  onSuccess: () => void | Promise<void>;
}

const AccountingEntryForm: React.FC<AccountingEntryFormProps> = ({
  initialAccountingEntriesGroup,
  initialErrors,
  onCancel,
  onSuccess,
}) => {
  const company = useSelector((state: RootState) => state.company.data.company);
  const [editingAccountingEntryGroup, setEditingAccountingEntryGroup] =
    useState<AccountingEntriesGroup>(
      initialAccountingEntriesGroup
        ? initialAccountingEntriesGroup
        : newAccountingEntryGroup()
    );
  const [items, setItems] = useState<UniqueListItem<AccountingEntry>[]>([]);
  const [orderDate, setOrderDate] = useState<string | moment.Moment>("");

  const [errors, setErrors] = useState<Errors>(
    initialErrors ? initialErrors : {}
  );
  const [submitting, setSubmitting] = useState(false);

  const onDatehange = (value: string | moment.Moment) => {
    const newDate = value as moment.Moment;
    if (newDate === undefined || !moment(value, true).isValid()) {
      return;
    }

    setOrderDate(value);

    if (moment.isMoment(value)) {
      setEditingAccountingEntryGroup({
        ...editingAccountingEntryGroup,
        date: value.toISOString(),
      });
    } else if (value !== undefined && value !== "") {
      setEditingAccountingEntryGroup({
        ...editingAccountingEntryGroup,
        date: value,
      });
    }
  };

  const onObsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingAccountingEntryGroup({
      ...editingAccountingEntryGroup,
      obs: e.target.value,
    });
  };

  const onAddEntryClick = () => {
    setItems((items) => {
      const newItems = [...items, newUniqueListItem<AccountingEntry>({})];
      return newItems;
    });
  };

  const onEntryChange = (entry: UniqueListItem<AccountingEntry>) => {
    setItems((items) => {
      const newItems = [...items];
      const index = newItems.findIndex((item) => {
        return item.uuid === entry.uuid;
      });

      if (index === -1) {
        return newItems;
      }

      newItems[index] = entry;

      return newItems;
    });
  };

  const onEntryDelete = (entry: UniqueListItem<AccountingEntry>) => {
    setItems((items) => {
      const newItems = [...items];

      const index = newItems.findIndex((item) => {
        return item.uuid === entry.uuid;
      });

      if (index === -1) {
        return newItems;
      }

      newItems.splice(index, 1);

      return newItems;
    });
  };

  const onSave = async () => {
    setSubmitting(true);
    let toSendAccountingEntryGroup = {
      ...editingAccountingEntryGroup,
      companyId: company.id,
    };
    const toSendItems: AccountingEntry[] = uniqueListToListItems(items);
    if (toSendItems.length === 0) {
      errorAlert("El asiento contable debe tener al menos una entrada");
      setSubmitting(false);
      return;
    }

    let updatedStatus = undefined;
    toSendItems.forEach((item) => {
      if (editingAccountingEntryGroup.id == undefined) {
        item.companyId = company.id;
        item.status = "completed";
      }
      if (item.date === undefined) {
        item.date = editingAccountingEntryGroup.date;
      }
      if (item.status === undefined) {
        item.status = "completed";
        updatedStatus = "completed";
      } else if (item.status === "pending") {
        item.status = "completed";
        updatedStatus = "completed";
      } else if (item.status === "ignored") {
        item.status = "modified";
        updatedStatus = "modified";
      } else if (item.status === "created") {
        item.status = "modified";
        updatedStatus = "modified";
      } else {
        updatedStatus = item.status;
      }
      if (!item.creditAmount) {
        item.creditAmount = 0;
      }
      if (!item.debitAmount) {
        item.debitAmount = 0;
      }
    });
    toSendAccountingEntryGroup.items = toSendItems;
    toSendAccountingEntryGroup.status = updatedStatus;

    let requestPromise: Promise<ItemRequestStatus<AccountingEntriesGroup>>;
    if (editingAccountingEntryGroup.id === undefined) {
      requestPromise = createItem<AccountingEntriesGroup>(
        "/accounting_entries_groups/create/",
        toSendAccountingEntryGroup
      );
    } else {
      requestPromise = updateItem<AccountingEntriesGroup>(
        `/accounting_entries_groups/${toSendAccountingEntryGroup.id}/`,
        toSendAccountingEntryGroup
      );
    }

    const accountingentryGroupStatus = await requestPromise;

    if (accountingentryGroupStatus.status !== SUCCESS) {
      if (accountingentryGroupStatus.errors !== undefined) {
        setErrors(accountingentryGroupStatus.errors);
      }

      let message = "Ha ocurrido un error!!";
      if (accountingentryGroupStatus.detail !== undefined) {
        message = accountingentryGroupStatus.detail;
      }
      errorAlert(message);
    } else {
      setErrors({});
      clearForm();
      onSuccess();
    }
    setSubmitting(false);
  };

  const onClose = () => {
    clearForm();
    onCancel();
  };

  const clearForm = () => {
    setEditingAccountingEntryGroup(newAccountingEntry());
    setOrderDate("");

    setErrors({});
  };

  useEffect(() => {
    setEditingAccountingEntryGroup(
      initialAccountingEntriesGroup
        ? initialAccountingEntriesGroup
        : newAccountingEntryGroup()
    );
    if (initialAccountingEntriesGroup?.date === undefined) {
      setOrderDate("");
    } else {
      setOrderDate(moment(initialAccountingEntriesGroup.date));
    }
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAccountingEntriesGroup]);

  useEffect(() => {
    setErrors(initialErrors ? initialErrors : {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialErrors]);

  const fetchItems = async () => {
    if (
      initialAccountingEntriesGroup === undefined ||
      initialAccountingEntriesGroup.id === undefined
    ) {
      setItems([]);
      return [];
    }
    const groupIdStr = initialAccountingEntriesGroup.id.toString();
    if (groupIdStr === undefined) {
      setItems([]);
      return [];
    }
    let additionalParams: Map<string, string> | undefined = new Map<
      string,
      string
    >();
    additionalParams.set("group_id", groupIdStr);
    const limit = 100;
    const offset = 0;
    const itemsStatus = await getList<AccountingEntry>(
      `/accounting_entries/`,
      limit,
      offset,
      additionalParams
    );
    if (itemsStatus.status === SUCCESS) {
      if (itemsStatus.data !== undefined) {
        setItems(listToUniqueListItems(itemsStatus.data.items));
      }
    } else {
      setItems([]);
    }
  };

  const itemErrors = getFieldErrors("items", errors) as Errors[];

  return (
    <>
      <fieldset disabled={submitting}>
        <CFormGroup>
          <CRow className={"mt-2"}>
            <CCol md={2}>
              <CLabel className={"mt-2"}>Fecha:</CLabel>
            </CCol>
            <CCol>
              <Datetime
                className="pl-0"
                onChange={onDatehange}
                value={orderDate}
                locale="es/PY"
                dateFormat="DD/MM/YYYY"
                timeFormat="HH:mm"
                closeOnSelect={true}
              ></Datetime>
              <FieldErrors
                errors={getFieldErrors("date", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Observación:</CLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                value={emptyValueOnUndefined(editingAccountingEntryGroup.obs)}
                onChange={onObsChange}
                placeholder="Observación"
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("obs", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        {items.map((entry, ix) => {
          return (
            <CRow key={entry.uuid}>
              <CCol>
                <AccountingEntryFormItem
                  value={entry.item}
                  onDelete={() => onEntryDelete(entry)}
                  onChange={(item) =>
                    onEntryChange({
                      uuid: entry.uuid,
                      item: item,
                    })
                  }
                  errors={ix < itemErrors.length ? itemErrors[ix] : {}}
                  initialEntry={entry.item}
                  initialAccount={entry.item?.account}
                />
              </CCol>
            </CRow>
          );
        })}
        {items.length === 0 ? (
          <CRow>
            <CCol>
              Agregue nuevas entradas al asiento con el boton + de la derecha
            </CCol>
          </CRow>
        ) : (
          <></>
        )}
        <CRow className="mb-1">
          <CCol>
            <CButton
              className="btn btn-primary float-right"
              onClick={onAddEntryClick}
            >
              <i className="fa fa-plus" />
            </CButton>
          </CCol>
        </CRow>
        <CFormGroup className="float-right">
          <CButtonGroup>
            <CButton type="button" color="secondary" onClick={onClose}>
              Atrás
            </CButton>
            <CButton type="submit" color="primary" onClick={onSave}>
              {submitting ? (
                <Spinner
                  animation="grow"
                  style={{
                    height: "17px",
                    width: "17px",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginRight: "10px",
                  }}
                />
              ) : (
                <></>
              )}
              {submitting ? "Guardando..." : "Guardar"}
            </CButton>
          </CButtonGroup>
        </CFormGroup>
      </fieldset>
    </>
  );
};

export default AccountingEntryForm;
